import { useMemo } from 'react';
import { Image, AlertTriangle, CheckCircle2, FileWarning, Scale, Maximize2 } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, getFileName } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface ImageIssue {
  type: 'error' | 'warning' | 'info';
  icon: React.ReactNode;
  title: string;
  description: string;
  entries: HAREntry[];
  potentialSavings?: number;
}

interface ImageStats {
  totalImages: number;
  totalSize: number;
  oversizedCount: number;
  uncompressedCount: number;
  modernFormatCount: number;
  legacyFormatCount: number;
}

export function ImageOptimization() {
  const { filteredEntries, selectEntry } = useHARStore();

  const imageEntries = useMemo(() => {
    return filteredEntries.filter(entry => 
      entry.response.content.mimeType?.includes('image/')
    );
  }, [filteredEntries]);

  const stats = useMemo((): ImageStats => {
    const totalSize = imageEntries.reduce((sum, e) => sum + (e.response.content.size || 0), 0);
    
    const oversized = imageEntries.filter(e => (e.response.content.size || 0) > 500 * 1024);
    
    const uncompressed = imageEntries.filter(e => {
      const size = e.response.content.size || 0;
      const hasCompression = e.response.headers.some(
        h => h.name.toLowerCase() === 'content-encoding'
      );
      return size > 50 * 1024 && !hasCompression;
    });

    const modernFormats = imageEntries.filter(e => {
      const mime = e.response.content.mimeType?.toLowerCase() || '';
      return mime.includes('webp') || mime.includes('avif') || mime.includes('jxl');
    });

    const legacyFormats = imageEntries.filter(e => {
      const mime = e.response.content.mimeType?.toLowerCase() || '';
      return mime.includes('png') || mime.includes('jpeg') || mime.includes('gif') || mime.includes('bmp');
    });

    return {
      totalImages: imageEntries.length,
      totalSize,
      oversizedCount: oversized.length,
      uncompressedCount: uncompressed.length,
      modernFormatCount: modernFormats.length,
      legacyFormatCount: legacyFormats.length,
    };
  }, [imageEntries]);

  const issues = useMemo(() => {
    if (imageEntries.length === 0) return [];

    const result: ImageIssue[] = [];

    // Oversized images
    const oversizedImages = imageEntries.filter(e => (e.response.content.size || 0) > 500 * 1024);
    if (oversizedImages.length > 0) {
      const totalOversizedBytes = oversizedImages.reduce((sum, e) => sum + (e.response.content.size || 0), 0);
      const potentialSavings = Math.floor(totalOversizedBytes * 0.6); // Estimate 60% savings with optimization

      result.push({
        type: 'error',
        icon: <Maximize2 className="w-4 h-4" />,
        title: `${oversizedImages.length} oversized image${oversizedImages.length > 1 ? 's' : ''}`,
        description: `Images over 500KB can significantly impact page load. Consider resizing, compressing, or using responsive images.`,
        entries: oversizedImages.sort((a, b) => (b.response.content.size || 0) - (a.response.content.size || 0)).slice(0, 5),
        potentialSavings,
      });
    }

    // Uncompressed images
    const uncompressedImages = imageEntries.filter(e => {
      const size = e.response.content.size || 0;
      const hasCompression = e.response.headers.some(
        h => h.name.toLowerCase() === 'content-encoding'
      );
      return size > 50 * 1024 && !hasCompression;
    });

    if (uncompressedImages.length > 0) {
      const totalUncompressedBytes = uncompressedImages.reduce((sum, e) => sum + (e.response.content.size || 0), 0);
      const potentialSavings = Math.floor(totalUncompressedBytes * 0.3); // Estimate 30% savings

      result.push({
        type: 'warning',
        icon: <FileWarning className="w-4 h-4" />,
        title: `${uncompressedImages.length} uncompressed image${uncompressedImages.length > 1 ? 's' : ''}`,
        description: `Images without compression are being served. Enable gzip/Brotli compression on your server.`,
        entries: uncompressedImages.sort((a, b) => (b.response.content.size || 0) - (a.response.content.size || 0)).slice(0, 5),
        potentialSavings,
      });
    }

    // Legacy format usage
    const legacyImages = imageEntries.filter(e => {
      const mime = e.response.content.mimeType?.toLowerCase() || '';
      const size = e.response.content.size || 0;
      return (mime.includes('png') || mime.includes('jpeg') || mime.includes('gif')) && size > 20 * 1024;
    });

    if (legacyImages.length > 0) {
      const totalLegacyBytes = legacyImages.reduce((sum, e) => sum + (e.response.content.size || 0), 0);
      const potentialSavings = Math.floor(totalLegacyBytes * 0.5); // Estimate 50% savings with WebP/AVIF

      result.push({
        type: 'info',
        icon: <Scale className="w-4 h-4" />,
        title: `${legacyImages.length} image${legacyImages.length > 1 ? 's' : ''} using legacy formats`,
        description: `Consider converting to WebP or AVIF for better compression. Use <picture> element for fallback.`,
        entries: legacyImages.sort((a, b) => (b.response.content.size || 0) - (a.response.content.size || 0)).slice(0, 5),
        potentialSavings,
      });
    }

    // Slow loading images
    const slowImages = imageEntries.filter(e => e.time > 1000);
    if (slowImages.length > 0) {
      result.push({
        type: 'warning',
        icon: <AlertTriangle className="w-4 h-4" />,
        title: `${slowImages.length} slow-loading image${slowImages.length > 1 ? 's' : ''}`,
        description: `Images taking over 1 second to load may impact user experience and Core Web Vitals.`,
        entries: slowImages.sort((a, b) => b.time - a.time).slice(0, 5),
      });
    }

    // Missing dimensions (LCP impact)
    const imagesWithoutDimensions = imageEntries.filter(e => {
      // Check if the entry was likely causing layout shifts
      // In real implementation, this would check for width/height attributes
      return e.time > 500 && (e.response.content.size || 0) > 100 * 1024;
    });

    if (imagesWithoutDimensions.length > 0) {
      result.push({
        type: 'info',
        icon: <Maximize2 className="w-4 h-4" />,
        title: `${imagesWithoutDimensions.length} image${imagesWithoutDimensions.length > 1 ? 's' : ''} may be causing layout shifts`,
        description: `Large images without explicit width/height attributes can cause Cumulative Layout Shift (CLS).`,
        entries: imagesWithoutDimensions.slice(0, 5),
      });
    }

    return result.sort((a, b) => {
      const typeOrder = { error: 0, warning: 1, info: 2 };
      return typeOrder[a.type] - typeOrder[b.type];
    });
  }, [imageEntries]);

  const totalPotentialSavings = useMemo(() => {
    return issues.reduce((sum, issue) => sum + (issue.potentialSavings || 0), 0);
  }, [issues]);

  if (imageEntries.length === 0) {
    return (
      <div className="card p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
          <Image className="w-6 h-6 text-slate-400" />
        </div>
        <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">No images found</h3>
        <p className="text-xs text-slate-500">This HAR doesn't contain any image requests.</p>
      </div>
    );
  }

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 space-y-4">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-2 mb-1">
              {stats.oversizedCount > 0 ? (
                <AlertTriangle className="w-4 h-4 text-red-500" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              )}
              <span className="text-xs text-slate-600 dark:text-slate-400">Oversized</span>
            </div>
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {stats.oversizedCount > 0 ? stats.oversizedCount : 'None'}
            </div>
            <div className="text-[10px] text-slate-500">
              {stats.oversizedCount > 0 ? '> 500KB each' : 'All images optimized'}
            </div>
          </div>

          <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-2 mb-1">
              <Image className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-slate-600 dark:text-slate-400">Modern Formats</span>
            </div>
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {stats.modernFormatCount}
            </div>
            <div className="text-[10px] text-slate-500">
              {stats.legacyFormatCount > 0 ? `${stats.legacyFormatCount} legacy formats` : 'All modern'}
            </div>
          </div>
        </div>

        {/* Potential Savings */}
        {totalPotentialSavings > 0 && (
          <div className="p-3 rounded-lg border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10">
            <div className="flex items-center gap-2 mb-1">
              <Scale className="w-4 h-4 text-green-500" />
              <span className="text-sm font-bold text-green-700 dark:text-green-400">
                Potential Savings: {formatBytes(totalPotentialSavings)}
              </span>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400">
              Estimated reduction in image payload size with optimization
            </p>
          </div>
        )}

        {/* Issues List */}
        {issues.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Optimization Opportunities
            </h3>
            {issues.map((issue, index) => (
              <div
                key={index}
                className={cn(
                  'p-3 rounded-lg border',
                  issue.type === 'error' && 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30',
                  issue.type === 'warning' && 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/30',
                  issue.type === 'info' && 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'p-1.5 rounded',
                    issue.type === 'error' && 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400',
                    issue.type === 'warning' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
                    issue.type === 'info' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                  )}>
                    {issue.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={cn(
                        'text-sm font-medium',
                        issue.type === 'error' && 'text-red-700 dark:text-red-400',
                        issue.type === 'warning' && 'text-yellow-700 dark:text-yellow-400',
                        issue.type === 'info' && 'text-blue-700 dark:text-blue-400'
                      )}>
                        {issue.title}
                      </h3>
                      {issue.potentialSavings && issue.potentialSavings > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300 font-bold">
                          Save {formatBytes(issue.potentialSavings)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {issue.description}
                    </p>
                    {issue.entries.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {issue.entries.map((entry, i) => (
                          <button
                            key={i}
                            onClick={() => selectEntry(entry)}
                            className="w-full text-left text-xs p-1.5 rounded bg-white/50 dark:bg-slate-900/30 hover:bg-white dark:hover:bg-slate-900/50 transition-colors flex items-center justify-between gap-2"
                          >
                            <span className="truncate text-slate-700 dark:text-slate-300">
                              {getFileName(entry.request.url)}
                            </span>
                            <span className="flex-shrink-0 text-slate-500">
                              {formatBytes(entry.response.content.size || 0)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {issues.length === 0 && (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">
              All images are optimized!
            </h3>
            <p className="text-xs text-slate-500">
              No optimization issues detected. Your images are well-optimized.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
